// This code is same as login-callback from @okta/react. Somehow this code wasn't working
// even after bumping the version. Maybe an issue with my system (node_modules). Will check with
// mac and change accordingly.
import { OktaAuth } from '@okta/okta-auth-js';
import { OktaContext } from '@okta/okta-react';
import { useNavigate } from '@rocketlane/rocket-router';
import { useEffect, useState, useContext } from 'react';
import { AccountAPI, AuthenticationServiceProvider } from 'api/account';
import { LS_TEMPLATE_ID_KEY, RoutePaths } from 'constant/constant';
import { useCustomAuth } from 'hooks/use-custom-auth';
import { useQueryParams } from 'hooks/use-query-params';
import { InitWorkerApi } from 'initWorker';
import { useAccountMetaQuery } from 'queries';
import { ActiveTab, getOkta } from 'services';
import {
  exchangeCodeWithId,
  authenticateWithId,
  redirectToOAuth,
} from 'utils/oauth-redirect';

export type OnAuthRequiredFunction = (
  oktaAuth: OktaAuth,
) => Promise<void> | void;

export type OnAuthResumeFunction = () => void;

interface LoginCallbackProps {
  errorComponent?: React.ComponentType<
    React.PropsWithChildren<{ error: Error }>
  >;
  onAuthResume?: OnAuthResumeFunction;
  loadingElement?: React.ReactElement;
}
const OktaError = ({ error }) => {
  if (error.name && error.message) {
    return (
      <p>
        {error.name}: {error.message}
      </p>
    );
  }

  return <p>{error.toString()}</p>;
};

export const LoginCallback: React.FC<
  React.PropsWithChildren<LoginCallbackProps>
> = ({ errorComponent, loadingElement = null, onAuthResume }) => {
  const replace = useNavigate();
  const oktaContext = useContext(OktaContext);
  const oktaAuth = oktaContext?.oktaAuth;
  const authState = oktaContext?.authState;
  const [callbackError, setCallbackError] = useState<Error | null>(null);
  const { setCustomAuth } = useCustomAuth();
  const { data } = useAccountMetaQuery();
  const params = useQueryParams();
  const code = params.get('code');
  const cookiesEnabled = data?.okta?.cookiesEnabled;
  const clientId = data?.okta?.clientId;
  const codeVerifier = sessionStorage.getItem('codeVerifier');

  const ErrorReporter = errorComponent || OktaError;

  useEffect(() => {
    if (codeVerifier) {
      // COGNITO FLOW
      const issuerDomain = data?.okta.issuerDomain;

      if (code && codeVerifier && issuerDomain) {
        AccountAPI.loginWithCognitoSSO(
          issuerDomain,
          code,
          `${window.location.origin}/implicit/callback`,
          codeVerifier,
          clientId,
        ).then((res: any) => {
          const { access_token } = res;

          window.location.href = `${
            window.location.origin
          }/login?token=${access_token}&cognito=${true}`;
        });
      }
    } else if (
      code &&
      data?.okta.authenticationServiceProvider ===
        AuthenticationServiceProvider.COGNITO
    ) {
      // This case is to support login from IdP initiated login
      // BUT ONLY ON COGNITO FLOW
      const issuerDomain = data?.okta.issuerDomain;

      if (code && issuerDomain) {
        AccountAPI.loginWithCognitoSSO(
          issuerDomain,
          code,
          `${window.location.origin}/implicit/callback`,
          undefined,
          clientId,
        ).then((res: any) => {
          const { access_token } = res;

          window.location.href = `${
            window.location.origin
          }/login?token=${access_token}&cognito=${true}`;
        });
      }
    } else {
      // OKTA FLOW
      if (onAuthResume) {
        onAuthResume();

        return;
      }

      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      oktaAuth
        .handleLoginRedirect()
        .then(() => {
          const templateId = localStorage.getItem(LS_TEMPLATE_ID_KEY);

          localStorage.removeItem(LS_TEMPLATE_ID_KEY);

          if (templateId) {
            replace(
              RoutePaths.documentTemplate.replaceAll(':templateId', templateId),
            );

            return;
          }

          if (authState?.isAuthenticated) {
            const activeTab = (ActiveTab.pop() ?? '/').replace(
              window.location.origin,
              '',
            );

            replace(activeTab);
          }
        })
        .catch(setCallbackError);

      return;
    }

    if (!code || !clientId) {
      return;
    }

    const issuerDomain = data?.okta.issuerDomain;
    const redirectUri = `${window.location.origin}/implicit/callback`;

    const loginWithCognito = async () => {
      try {
        const res = await AccountAPI.loginWithCognitoSSO(
          issuerDomain,
          code,
          redirectUri,
          codeVerifier,
          clientId,
        );
        const { access_token } = res;

        window.location.href = `${window.location.origin}/login?token=${access_token}&cognito=true`;
      } catch (error) {
        setCallbackError(error as Error);
      }
    };

    const loginWithNitro = async () => {
      try {
        const res = await exchangeCodeWithId(
          code,
          redirectUri,
          // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
          codeVerifier,
          clientId,
        );
        const { accessToken } = res;

        await authenticateWithId(accessToken);

        setCustomAuth(true);
        getOkta()?.authStateManager.updateAuthState();
        InitWorkerApi.setData();

        const activeTab = (ActiveTab.pop() ?? '/').replace(
          window.location.origin,
          '',
        );

        // Use hard navigation to clear init worker's cached rejections from pre-login 401s.
        // SPA navigation (replace) would keep the stale cached state, causing LazyBootApp
        // to stay at PageLoading forever since useAccountDataQuery sees the cached rejection.
        window.location.href = activeTab;
      } catch {
        redirectToOAuth(clientId);
      }
    };

    if (cookiesEnabled) {
      loginWithNitro();
    } else {
      loginWithCognito();
    }
  }, [
    oktaAuth,
    onAuthResume,
    replace,
    authState,
    codeVerifier,
    data?.okta,
    code,
    clientId,
    cookiesEnabled,
    setCustomAuth,
  ]);

  const authError = authState?.error;
  const displayError = callbackError || authError;

  if (displayError) {
    return <ErrorReporter error={displayError} />;
  }

  return loadingElement;
};
